import { configureStore, combineReducers, ThunkDispatch } from '@reduxjs/toolkit';
import { persistStore, persistReducer, createMigrate, MigrationManifest } from 'redux-persist';
import createWebStorage from 'redux-persist/lib/storage/createWebStorage';
import authReducer from './slices/authSlice';
import languageReducer from './slices/languageSlice';
import lienReducer from './slices/lienSlice';
import uiReducer from './slices/uiSlice';
import dashboardConfigReducer from './slices/dashboardConfigSlice';
import lienSummaryConfigReducer from './slices/lienSummaryConfigSlice';
import companyReducer from './slices/companySlice';
import historicalReportReducer from './slices/historicalReportSlice';
import systemReducer from './slices/systemSlice';

// Create a noop storage for SSR
const createNoopStorage = () => {
  return {
    getItem(_key: string) {
      return Promise.resolve(null);
    },
    setItem(_key: string, value: any) {
      return Promise.resolve(value);
    },
    removeItem(_key: string) {
      return Promise.resolve();
    },
  };
};

// Use localStorage if available (client-side), otherwise use noop storage (server-side)
const storage = typeof window !== 'undefined' ? createWebStorage('local') : createNoopStorage();

// Redux Persist Migrations
// Increment version when making breaking changes to persisted state structure
const migrations: MigrationManifest = {
  0: (state: any) => {
    // Initial state - no migration needed
    return state;
  },
  1: (state: any) => {
    // Migration for version 1: Reset UI state to clear old cached styles
    return {
      ...state,
      ui: {
        logo: state.ui?.logo || null,
      },
      // Keep other slices intact
      language: state.language,
      dashboardConfig: state.dashboardConfig,
      lienSummaryConfig: state.lienSummaryConfig,
      company: state.company,
      historicalReport: state.historicalReport,
      system: state.system,
    };
  },
  // Add future migrations here:
  // 2: (state: any) => {
  //   return {
  //     ...state,
  //     // Your migration logic
  //   };
  // },
};

// Persist configuration
// NOTE: 'auth' is NOT persisted here - it's managed via sessionStorage for tab isolation
// Each tab maintains its own auth state independently
const persistConfig = {
  key: 'root',
  version: 1, // Increment this when you want to trigger migrations
  storage,
  whitelist: ['language', 'ui', 'dashboardConfig', 'lienSummaryConfig', 'company', 'historicalReport', 'system'], // Persist non-auth slices only
  migrate: createMigrate(migrations, { debug: false }),
};

// Root reducer
const rootReducer = combineReducers({
  auth: authReducer,
  language: languageReducer,
  lien: lienReducer,
  ui: uiReducer,
  dashboardConfig: dashboardConfigReducer,
  lienSummaryConfig: lienSummaryConfigReducer,
  company: companyReducer,
  historicalReport: historicalReportReducer,
  system: systemReducer,
});

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

// Create persistor
export const persistor = persistStore(store);

// Export types
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store; 

export type AppThunkDispatch = ThunkDispatch<RootState, unknown, any>;
